// apps/web/src/components/visitors/VisitorCard/VisitorActions.tsx
import React from 'react';
import { Box, IconButton, Tooltip, Button, SxProps, Theme } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  FilterList as HistoryIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import CheckInButton from '../CheckInButton';
import { Visitor } from '@gym-management/shared';

interface VisitorActionsProps {
  visitor: Visitor;
  onEdit?: (visitor: Visitor) => void;
  onDelete?: (id: string) => void;
  onViewDetails?: (visitor: Visitor) => void;
  onShowQR?: (visitor: Visitor) => void;
  onShowHistory?: (visitor: Visitor) => void;
  onCheckIn?: () => void;
  onCheckOut?: () => void;
  isCheckedIn?: boolean;
  variant?: 'icons' | 'buttons' | 'table' | 'full';
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

const VisitorActions: React.FC<VisitorActionsProps> = ({
  visitor,
  onEdit,
  onDelete,
  onViewDetails,
  onShowQR,
  onShowHistory,
  onCheckIn,
  onCheckOut,
  isCheckedIn = false,
  variant = 'icons',
  size = 'small',
  sx,
}) => {
  const theme = useTheme();
  const iconSize = size === 'small' ? 'small' : 'medium';

  // --- Вариант для таблицы (с кнопкой чекина и иконками) ---
  if (variant === 'table') {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, ...sx }}
      >
        {visitor.active && onCheckIn && onCheckOut && (
          <CheckInButton
            visitorId={visitor.id}
            visitorName={visitor.name}
            isCheckedIn={isCheckedIn}
            onCheckIn={onCheckIn}
            onCheckOut={onCheckOut}
            size="small"
            variant={isCheckedIn ? 'contained' : 'outlined'}
            showLabel={true}
            sx={{ minWidth: 70, height: 32, fontSize: '0.7rem' }}
          />
        )}
        {onViewDetails && (
          <Tooltip title="Подробнее">
            <IconButton size="small" onClick={() => onViewDetails(visitor)}>
              <VisibilityIcon fontSize={iconSize} />
            </IconButton>
          </Tooltip>
        )}
        {onShowHistory && (
          <Tooltip title="История посещений">
            <IconButton size="small" onClick={() => onShowHistory(visitor)}>
              <HistoryIcon fontSize={iconSize} />
            </IconButton>
          </Tooltip>
        )}
        {onShowQR && (
          <Tooltip title="Показать QR код">
            <IconButton size="small" onClick={() => onShowQR(visitor)}>
              <QrCodeIcon fontSize={iconSize} />
            </IconButton>
          </Tooltip>
        )}
        {onEdit && (
          <Tooltip title="Редактировать">
            <IconButton size="small" onClick={() => onEdit(visitor)}>
              <EditIcon fontSize={iconSize} />
            </IconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title="Удалить">
            <IconButton size="small" onClick={() => onDelete(visitor.id)} color="error">
              <DeleteIcon fontSize={iconSize} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  // --- Вариант для плитки (иконки, кроме чекина) ---
  if (variant === 'buttons') {
    return (
      <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center', ...sx }}>
        {visitor.active && onCheckIn && onCheckOut && (
          <CheckInButton
            visitorId={visitor.id}
            visitorName={visitor.name}
            isCheckedIn={isCheckedIn}
            onCheckIn={onCheckIn}
            onCheckOut={onCheckOut}
            size={size}
            variant={isCheckedIn ? 'contained' : 'outlined'}
            showLabel={true}
            sx={{ flex: 1, minWidth: 0, fontSize: '0.7rem', py: 0.75, height: 32 }}
          />
        )}
        {onViewDetails && (
          <Tooltip title="Подробнее">
            <IconButton size="small" onClick={() => onViewDetails(visitor)}>
              <VisibilityIcon fontSize={iconSize} />
            </IconButton>
          </Tooltip>
        )}
        {onShowQR && (
          <Tooltip title="QR-код">
            <IconButton size="small" onClick={() => onShowQR(visitor)}>
              <QrCodeIcon fontSize={iconSize} />
            </IconButton>
          </Tooltip>
        )}
        {onEdit && (
          <Tooltip title="Редактировать">
            <IconButton size="small" onClick={() => onEdit(visitor)}>
              <EditIcon fontSize={iconSize} />
            </IconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title="Удалить">
            <IconButton
              size="small"
              onClick={() => onDelete(visitor.id)}
              sx={{
                color: 'error.main',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.04),
                },
              }}
            >
              <DeleteIcon fontSize={iconSize} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  // --- Вариант для полного режима ---
  if (variant === 'full') {
    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ...sx }}>
        {onEdit && (
          <Button
            variant="outlined"
            onClick={() => onEdit(visitor)}
            startIcon={<EditIcon />}
            sx={{ flex: '1 1 140px', height: 36 }}
          >
            Редактировать
          </Button>
        )}
        {onShowQR && (
          <Button
            variant="contained"
            onClick={() => onShowQR(visitor)}
            startIcon={<QrCodeIcon />}
            sx={{ flex: '1 1 140px', height: 36 }}
          >
            QR-код
          </Button>
        )}
        {onShowHistory && (
          <Button
            variant="outlined"
            onClick={() => onShowHistory(visitor)}
            startIcon={<HistoryIcon />}
            sx={{ flex: '1 1 140px', height: 36 }}
          >
            История
          </Button>
        )}
        {visitor.active && onCheckIn && onCheckOut && (
          <CheckInButton
            visitorId={visitor.id}
            visitorName={visitor.name}
            isCheckedIn={isCheckedIn}
            onCheckIn={onCheckIn}
            onCheckOut={onCheckOut}
            size="medium"
            variant="contained"
            showLabel={true}
            sx={{ flex: '1 1 140px', height: 36, minHeight: 36 }}
          />
        )}
        {onDelete && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => onDelete(visitor.id)}
            startIcon={<DeleteIcon />}
            sx={{ flex: '1 1 120px', height: 36 }}
          >
            Удалить
          </Button>
        )}
      </Box>
    );
  }

  // --- Вариант по умолчанию (только иконки) ---
  return (
    <Box sx={{ display: 'flex', gap: 0.5, ...sx }}>
      {onViewDetails && (
        <IconButton size="small" onClick={() => onViewDetails(visitor)}>
          <VisibilityIcon fontSize={iconSize} />
        </IconButton>
      )}
      {onShowHistory && (
        <IconButton size="small" onClick={() => onShowHistory(visitor)}>
          <HistoryIcon fontSize={iconSize} />
        </IconButton>
      )}
      {onShowQR && (
        <IconButton size="small" onClick={() => onShowQR(visitor)}>
          <QrCodeIcon fontSize={iconSize} />
        </IconButton>
      )}
      {onEdit && (
        <IconButton size="small" onClick={() => onEdit(visitor)}>
          <EditIcon fontSize={iconSize} />
        </IconButton>
      )}
      {onDelete && (
        <IconButton size="small" onClick={() => onDelete(visitor.id)} color="error">
          <DeleteIcon fontSize={iconSize} />
        </IconButton>
      )}
    </Box>
  );
};

export default VisitorActions;
